<template>
  <v-container fluid class="container">
    <Breadcrumbs :items="breadcrumbs" />

    <v-toolbar flat>
      <ejs-dropdownlist
        id="assetTypeDropdownlist"
        ref="assetTypeDropdown"
        :dataSource="assetTypeDropdownDataSource"
        :change="onAssetTypeDropdownChange"
        v-model="assetTypeDropdown"
        placeholder="Asset Type"
        width="200"
      />
      <v-spacer />
      <v-btn color="primary" @click="excelDownloadClick">
        <v-icon left>mdi-file-excel</v-icon>
        Export to Excel
      </v-btn>
    </v-toolbar>

    <v-spacer />

    <ejs-grid
      ref="grid"
      v-if="items.length && periods.length"
      :key="`grid-${assetTypeDropdownValue}`"
      :dataSource="items"
      :filterSettings="{type: 'Menu'}"
      :searchSettings='searchOptions'
      :allowFiltering='true'
      :allowPaging='true'
      :pageSettings='pageSettings'
      :height="560"
      :allowExcelExport="true"
      :allowTextWrap="true"
      :excelHeaderQueryCellInfo="excelHeader"
    >
      <e-columns>
        <!-- Static columns for CAPEX -->
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="contract" headerText="Invent. br." width="120" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="finrep_pl_code" headerText="FINREP P&L Code" width="130" format="n0" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="dep_amount" headerText="Dep Amount" width="120" format="n0" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="trenutna_vrijednost" headerText="Trenutna Vrijednost" width="150" format="n0" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="useful_life" headerText="Useful Life" width="100" format="n2" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="depreciation_rate" headerText="Depreciation Rate" width="130" format="n2" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'capex'" field="capex_total" headerText="CAPEX Total" width="120" format="n2" textAlign="Right" />

        <!-- Static columns for OL -->
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="contract" headerText="Contract" width="150" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="contract_period" headerText="Contract Period" width="130" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="finrep_bs" headerText="FINREP BS" width="120" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="finrep_pl" headerText="FINREP P&L" width="120" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="maturity" headerText="Maturity" width="100" format="n0" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="useful_life" headerText="Useful Life" width="100" format="n0" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="residual_value" headerText="Residual Value" width="130" format="n2" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="capex_total" headerText="CAPEX Total" width="120" format="n2" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="end_value" headerText="End Value" width="120" format="n2" textAlign="Right" />
        <e-column v-if="assetTypeDropdownValue === 'ol'" field="gain_loss" headerText="Gain/Loss" width="120" format="n2" textAlign="Right" />

        <!-- Dynamic column groups -->
        <e-column textAlign="center" headerText="Book Value" :allowSorting="false" :columns="bookValueColumns" />
        <e-column textAlign="center" headerText="Depreciation" :allowSorting="false" :columns="depreciationColumns" />
      </e-columns>
    </ejs-grid>

    <Snackbar :text="notificationText" :type="notificationType" />

    <v-overlay :value="loader" :z-index="1000">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
  GridPlugin,
  ExcelExport,
  Filter,
  Page,
} from '@syncfusion/ej2-vue-grids';
import Breadcrumbs from '@/components/Breadcrumbs';
import Snackbar from '@/components/Snackbar';
import api from '@/api/lbt-api';

Vue.use(GridPlugin);

export default {
  name: 'Assets',
  components: { Breadcrumbs, Snackbar },
  data() {
    return {
      notificationText: '',
      notificationType: null,
      loader: false,
      periods: [],
      items: [],
      assetTypeDropdown: 'CAPEX',
      assetTypeDropdownValue: 'capex',
      assetTypeDropdownDataSource: [
        'CAPEX',
        'Operating Lease',
      ],
      searchOptions: {
        fields: ['placement_period', 'contract', 'am_stopa', 'finrep_pl_code', 'dep_amount', 'trenutna_vrijednost'],
        operator: 'contains',
        key: '',
        ignoreCase: true,
      },
      pageSettings: {
        pageSize: 50,
        pageSizes: [25, 50, 100, 200, 'All'],
        enableQueryString: false,
      },
      breadcrumbs: [
        { translationKey: 'breadcrumbs_plannings', route: '/plannings', disabled: false },
        { translationKey: 'assets', disabled: true },
      ],
      // Dynamic column arrays
      bookValueColumns: [],
      depreciationColumns: [],
    };
  },
  computed: {
    ...mapGetters('translations', ['translate']),
    ...mapState('plannings', ['activePlanning']),
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loader = true;
      try {
        let payload;
        if (this.assetTypeDropdownValue === 'capex') {
          payload = await api.getExistingCapexForPlanning(this.$route.params.planningId);
        } else {
          payload = await api.getExistingOlForPlanning(this.$route.params.planningId);
        }

        // Transform data based on asset type
        const dataKey = this.assetTypeDropdownValue === 'capex' ? 'existing_capex' : 'existing_ol_entries';
        const rawData = payload[dataKey] || [];

        // Extract periods from the first item
        if (rawData.length > 0) {
          const firstItem = rawData[0];
          const bookValueData = firstItem.book_value || [];
          this.periods = bookValueData.map((period, index) => period.period || `Period_${index}`);
        } else {
          this.periods = [];
        }

        // Transform data for grid display
        const transformedItems = rawData.map((item) => {
          const row = {
            // Common fields
            capex_total: item.capex_total || 0,
            useful_life: item.useful_life || 0,
          };

          // CAPEX specific fields
          if (this.assetTypeDropdownValue === 'capex') {
            row.contract = item.contract || 0;
            row.placement_period = item.placement_period || '';
            row.am_stopa = item.am_stopa || 0;
            row.finrep_pl_code = item.finrep_pl_code || 0;
            row.dep_amount = item.dep_amount || 0;
            row.trenutna_vrijednost = item.trenutna_vrijednost || 0;
            row.depreciation_rate = item.depreciation_rate || 0;
          }

          // OL specific fields
          if (this.assetTypeDropdownValue === 'ol') {
            row.id = item.id || '';
            row.contract = item.contract || '';
            row.contract_period = item.contract_period || '';
            row.finrep_bs = item.finrep_bs || 0;
            row.finrep_pl = item.finrep_pl || 0;
            row.maturity = item.maturity || 0;
            row.residual_value = item.residual_value || 0;
            row.end_value = item.end_value || 0;
            row.gain_loss = item.gain_loss || 0;
          }

          // Transform period-based arrays to flat properties
          // Book values
          if (item.book_value && Array.isArray(item.book_value)) {
            item.book_value.forEach((periodData, idx) => {
              row[`book_value_${idx}`] = periodData.value || 0;
            });
          }

          // Depreciation values
          if (item.depreciation && Array.isArray(item.depreciation)) {
            item.depreciation.forEach((periodData, idx) => {
              row[`depreciation_${idx}`] = periodData.value || 0;
            });
          }

          return row;
        });

        this.items = transformedItems;

        // Build dynamic columns after data is ready
        this.buildAllColumns();

        // Force grid refresh
        this.$nextTick(() => {
          if (this.$refs.grid && this.$refs.grid.ej2Instances) {
            const grid = this.$refs.grid.ej2Instances[0];
            if (grid && grid.refresh) {
              grid.refresh();
            }
          }
        });
      } catch (e) {
        this.notificationText = e.message || 'Failed to load assets data';
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 3000);
      } finally {
        this.loader = false;
      }
    },

    // Build book value columns dynamically
    buildBookValueColumns() {
      this.bookValueColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `book_value_${index}`,
          headerText: period,
          width: 120,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-1-first' };
        } else {
          colDef.customAttributes = { class: 'group-1' };
        }
        this.bookValueColumns.push(colDef);
      });
    },

    // Build depreciation columns dynamically
    buildDepreciationColumns() {
      this.depreciationColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `depreciation_${index}`,
          headerText: period,
          width: 120,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-2-first' };
        } else {
          colDef.customAttributes = { class: 'group-2' };
        }
        this.depreciationColumns.push(colDef);
      });
    },

    onAssetTypeDropdownChange({ value }) {
      // Map display text to API values
      const mappings = {
        CAPEX: 'capex',
        'Operating Lease': 'ol',
      };
      this.assetTypeDropdownValue = mappings[value] || 'capex';
      this.fetchData();
    },

    async excelDownloadClick() {
      const grid = document.getElementsByClassName('e-grid')[0].ej2_instances[0];
      if (!grid) {
        return;
      }

      const suffixByHeader = {
        'Book Value': '_BV',
        Depreciation: '_AM',
      };

      // Flatten stacked headers in place so Excel gets a single header row
      // (no "Book Value" / "Depreciation" parent row) and period columns are
      // suffixed with _BV / _AM.
      const originalColumns = grid.columns;
      const flatColumns = [];
      const headerTextSnapshots = [];

      originalColumns.forEach((col) => {
        if (col.columns && col.columns.length) {
          const suffix = suffixByHeader[col.headerText] || '';
          col.columns.forEach((child) => {
            const m = String(child.field || '').match(/_(\d+)$/);
            const idx = m ? parseInt(m[1], 10) : 0;
            const periodLabel = this.periods[idx] || child.headerText || '';
            headerTextSnapshots.push({ column: child, headerText: child.headerText });
            // eslint-disable-next-line no-param-reassign
            child.headerText = `${periodLabel}${suffix}`;
            flatColumns.push(child);
          });
        } else {
          flatColumns.push(col);
        }
      });

      grid.columns = flatColumns;
      try {
        await grid.excelExport({
          exportType: 'AllPages',
          fileName: `Assets - ${this.assetTypeDropdown} - Planning - ${this.activePlanning.year}-${this.activePlanning.month}.xlsx`,
        });
      } finally {
        headerTextSnapshots.forEach((snap) => {
          // eslint-disable-next-line no-param-reassign
          snap.column.headerText = snap.headerText;
        });
        grid.columns = originalColumns;
        if (typeof grid.refreshColumns === 'function') {
          grid.refreshColumns();
        }
      }
    },

    excelHeader() {
      // Period column headerText is already the final value (period name, plus
      // the _BV / _AM suffix during export), so no per-cell rewrite is needed.
    },

    // Build all column types
    buildAllColumns() {
      this.buildBookValueColumns();
      this.buildDepreciationColumns();
    },
  },
  provide: { grid: [ExcelExport, Filter, Page] },
};
</script>

<style>
@import '~@syncfusion/ej2-vue-treegrid/styles/material.css';

.e-grid .e-headercell.highlight-cell,
.highlight-cell {
  background: #eeeeee;
}

.e-valid-input {
  margin-right: 10px;
}

.e-headercelldiv {
  text-align: center !important;
}

/* Column group dividers - add thicker borders between groups */
.e-grid .e-stackedheadercell {
  border-left: 3px solid #1976d2 !important;
  background: linear-gradient(to bottom, #e3f2fd 0%, #bbdefb 100%);
  font-weight: 600;
}

/* First column group doesn't need left border */
.e-grid .e-stackedheadercell:first-of-type {
  border-left: none !important;
}

/* Alternating background colors for column groups */
.e-grid .e-stackedheadercell:nth-child(odd) {
  background: linear-gradient(to bottom, #fff3e0 0%, #ffe0b2 100%);
}

.e-grid .e-stackedheadercell:nth-child(even) {
  background: linear-gradient(to bottom, #e8f5e9 0%, #c8e6c9 100%);
}

/* Apply dividers to first column of each group */
.e-grid .e-rowcell.group-1-first,
.e-grid .e-rowcell.group-2-first {
  border-left: 3px solid #1976d2 !important;
}

/* Apply background colors to all columns in each group */
/* Group 1 - Book Value */
.e-grid .e-row .e-rowcell.group-1,
.e-grid .e-row .e-rowcell.group-1-first {
  background-color: #fff9f5;
}
.e-grid .e-altrow .e-rowcell.group-1,
.e-grid .e-altrow .e-rowcell.group-1-first {
  background-color: #fff5eb;
}

/* Group 2 - Depreciation */
.e-grid .e-row .e-rowcell.group-2,
.e-grid .e-row .e-rowcell.group-2-first {
  background-color: #f5fdf5;
}
.e-grid .e-altrow .e-rowcell.group-2,
.e-grid .e-altrow .e-rowcell.group-2-first {
  background-color: #edfaed;
}
</style>
